// "My History" — the user's own past questions (chat_query_logs), newest
// first, 20 per page. Answers are not stored (the chat is stateless), so
// each entry offers "Ask again": it reopens the right chat with the
// question pre-filled, ready to resend.
import { useEffect, useState } from 'react';
import { fetchHistory } from './historyApi';
import ChatView, { equipmentTitle, GENERAL_CHAT_ICON } from '../chat/ChatView';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export default function MyHistory({ onBack }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null); // { items, page, page_size, total }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When set, the section shows the chat with the question pre-filled.
  const [chat, setChat] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchHistory(page)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(e => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  if (chat) {
    return (
      <ChatView
        equipmentPath={chat.equipmentPath}
        title={chat.title}
        initialInput={chat.initialInput}
        onBack={() => setChat(null)}
      />
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div style={{ fontFamily: 'var(--sans)', color: 'var(--text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <button
          className="btn btn-ghost"
          onClick={onBack}
          style={{ padding: '9px 16px', fontSize: 12, minHeight: 40 }}
        >
          ←
        </button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>🕘 My History</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            Your past questions — answers aren't stored, but you can ask again anytime.
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>Loading…</div>
      )}
      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      {!loading && !error && data && (
        <>
          {data.items.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)', fontSize: 13 }}>
              No questions yet — ask something in any chat and it will show up here.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.items.map(item => {
              const isGeneral = item.equipment_path === 'general';
              return (
                <div
                  key={item.id}
                  style={{
                    background: 'var(--surface-1, #ffffff)',
                    border: '1px solid var(--border-soft, #eceff6)',
                    borderRadius: 14,
                    padding: '12px 14px',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, lineHeight: 1.55, wordBreak: 'break-word' }}>
                      {item.question}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 10,
                        letterSpacing: 0.5,
                        color: 'var(--text-3)',
                        marginTop: 6,
                        display: 'flex',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>
                        {isGeneral ? GENERAL_CHAT_ICON : '📚'}{' '}
                        {equipmentTitle(item.equipment_path).toUpperCase()}
                      </span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-ghost"
                    onClick={() =>
                      setChat({
                        equipmentPath: item.equipment_path,
                        title: equipmentTitle(item.equipment_path),
                        initialInput: item.question,
                      })
                    }
                    style={{ padding: '8px 14px', fontSize: 11.5, whiteSpace: 'nowrap' }}
                  >
                    ↻ Ask again
                  </button>
                </div>
              );
            })}
          </div>

          {data.total > data.page_size && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                marginTop: 16,
              }}
            >
              <button
                className="btn btn-ghost"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '7px 14px', fontSize: 12 }}
              >
                ← Prev
              </button>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
                PAGE {page} / {totalPages}
              </span>
              <button
                className="btn btn-ghost"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '7px 14px', fontSize: 12 }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
